<template>
  <form @:submit.prevent="submit" class="form-spacing">
    <v-text-field
      v-model="name"
      label="Name"
      required
      :readonly="isDisabled"
      type="text"
      :autofocus="true"
      :error-messages="nameErrors"
      @blur="v$.name.$touch()"
    />

    <v-text-field
      v-model="pepperUserID"
      label="Pepper user ID"
      :readonly="isDisabled"
      required
      type="text"
      :error-messages="pepperUserIDErrors"
      @blur="v$.pepperUserID.$touch()"
    />

    <magic-container>
      <platform-select
        v-model="country"
        :is-multiple="false"
        :readonly="isDisabled"
        :error-messages="countryErrors"
        :hide-details="false"
      />
      <v-select
        v-if="isEditModal"
        v-model="status"
        label="Status"
        item-title="name"
        return-object
        :readonly="isDisabled"
        :items="statuses"
      />
    </magic-container>

    <div class="form-actions">
      <v-btn
        variant="text"
        class="trigger go-back"
        @click="cancel"
      >
        Cancel
      </v-btn>

      <v-btn
        class="trigger register"
        @click="submit"
        color="primary"
      >
        {{ submitActionText }}
      </v-btn>
    </div>
  </form>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { isObj, isStr } from '@/utils';
import fetchIfNotInStoreMixin from '@/mixins/fetchIfNotInStore';

export default {
  mixins: [fetchIfNotInStoreMixin],
  setup () {
    return { v$: useVuelidate() }
  },
  validations() {
    return {
      name: {
        required,
      },
      pepperUserID: {
        required,
      },
      country: {
        required,
      },
    };
  },
  data() {
    return {
      id: null,
      name: '',
      pepperUserID: '',
      country: null,
      status: null,
      isLoading: false,
      isDisabled: false,
    };
  },
  computed: {
    statuses() {
      return this.$store.getters['businesses/statuses'];
    },
    isEditModal() {
      return this.$route.name === 'creator-program-businesses-details'
    },
    submitActionText() {
      if (this.isEditModal) {
        return this.$t('change_business');
      } else {
        return this.$t('create_business');
      }
    },
    countries() {
      return this.$store.getters['config/countries'];
    },
    nameErrors() {
      return this.v$.name.$errors.map(error => {
        if (error.$validator === 'required') {
          return 'Name is required';
        }
        return error.$message;
      });
    },
    pepperUserIDErrors() {
      return this.v$.pepperUserID.$errors.map(error => {
        if (error.$validator === 'required') {
          return 'Pepper User ID is required';
        }
        return error.$message;
      });
    },
    countryErrors() {
      return this.v$.country.$errors.map(error => {
        if (error.$validator === 'required') {
          return 'Country is required';
        }
        return error.$message;
      });
    },
  },
  watch: {
    '$route.params.id': {
      handler(nextVal) {
        if (nextVal) {
          this.fetchIfNotInStore(nextVal,'creatorProgram', 'businesses', 'getBusiness').then((data) => {
            this.clear();
            this.onReceiveBusiness(data);
          });
        }
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    this.onReceiveBusiness(this.businessProfile);
    if (this.statuses) {
      this.$store.dispatch('businesses/getStatuses');
    }
  },
  methods: {
    onReceiveBusiness(nextBusiness) {
      if (isObj(nextBusiness)) {
        this.extractItemData(nextBusiness);
      }
    },
    getAllCountries() {
      return this.$store.dispatch('config/getCountries').then((countries) => {
        const germany = countries.find(({ code }) => code === 'DE');
        return countries;
      });
    },
    submit() {
      this.v$.$touch();

      if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
        return false;
      }

      this.submitBusiness();
    },
    clear() {
      this.v$.$reset();
      this.name = '';
      this.pepperUserID = '';
      this.country = '';
    },
    cancel() {
      this.clear();
      this.$router.push('/creator-program/businesses');
    },
    submitBusiness() {
      const businessProfile = {
        id: this.id,
        name: this.name,
        core_user_id: this.pepperUserID,
        country: this.country,
        status: this.status?.id,
      };

      this.isLoading = true;

      const action = this.isEditModal ? 'creatorProgram/updateBusiness' : 'creatorProgram/createBusiness';

      return this.$store
        .dispatch(action, businessProfile)
        .then(res => {
          this.$router.push({ name: 'creator-program-businesses', query: { status: 'success' } });
          return res;
        })
        .catch((err) => {
          this.isAddingBusiness = false;
          return err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    extractItemData(businessProfile) {
      const {
        id,
        name,
        core_user_id,
        country,
        status
      } = businessProfile;

      // we need countries list for component to work
      if (this.countries.length === 0) {
        this.getAllCountries();
      }

      this.id = id;
      this.name = name;
      this.pepperUserID = core_user_id;
      this.country = country?.code;
      this.status = status;
    },
    canSubmitRequest() {
      return this.name !== ''
        && this.pepperUserID !== ''
        && isStr(this.country)
        && !this.isSubmitted;
    },
  },
};
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/theme' as *;

.form-actions {
  align-items: center;
  display: flex;
  justify-content: flex-end;

  .v-btn {
    margin: 0 12px 0 0;

    &:last-child {
      margin: 0;
    }
  }
}
</style>
